import { BreakpointObserver } from '@angular/cdk/layout';
import { APP_BASE_HREF, AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Meta } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { AVAILABLE_COUNTRIES } from '@app/core/const/country';
import { AppActions } from '@app/store/actions';
import * as fromRoot from '@app/store/reducers';
import { TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

import { AVAILABLE_LANGS, AvailableLang } from './core/const/i18n';
import { LayoutComponent } from './core/containers/layout/layout.component';

@Component({
  selector: 'am-root',
  template: `
    @if (urlPath$ | async; as urlPath) {
      @if (urlPath !== '/login') {
        <am-layout [urlPath]="urlPath"></am-layout>
      } @else {
        <router-outlet></router-outlet>
      }
    }
  `,
  imports: [LayoutComponent, RouterOutlet, AsyncPipe],
})
export class AppComponent {
  urlPath$: Observable<string>;

  private readonly _baseHref = inject(APP_BASE_HREF);
  private readonly _store = inject<Store<fromRoot.State>>(Store);
  private readonly _breakpointObserver = inject(BreakpointObserver);
  private readonly _translocoService = inject(TranslocoService);
  private readonly _meta = inject(Meta);

  constructor() {
    const router = inject(Router);

    this._meta.addTag({ name: 'app-version', content: '1.1.0' });

    this.urlPath$ = router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => router.url)
    );

    this._breakpointObserver
      .observe('(min-width: 1281px)')
      .pipe(
        map(breakpointState => breakpointState.matches),
        distinctUntilChanged(),
        takeUntilDestroyed()
      )
      .subscribe(isScreenWide => {
        this._store.dispatch(AppActions.isScreenWideChanged({ isScreenWide }));
      });

    const country = AVAILABLE_COUNTRIES.find(availableCountry => availableCountry === this._baseHref.replace('/', ''));
    this._store.dispatch(AppActions.setCountry({ country }));
    this._translocoService.setAvailableLangs(AVAILABLE_LANGS[country]);
    const currentLang = AVAILABLE_LANGS[country].find(lang => lang === this._translocoService.getActiveLang());
    this._store.dispatch(
      AppActions.updateLanguage({
        currentLang: currentLang ? currentLang : AvailableLang.en,
      })
    );
  }
}
